<template>
  <AdaptiveDialog v-model:visible="visible" modal pt:content="p-0" class="md:w-300 mt-10 lg:mt-20" pt:header="px-4 py-1"
    @show="onShow" @hide="onHide">
    <template #header>
      {{ $t('modal.marketSpreadAlerts.title') }}
    </template>
    <Message class="rounded-none border-t-0 border-b-0" size="small" severity="success">{{
      $t("modal.marketSpreadAlerts.info") }}</Message>
    <SrDataTable size="small" :columns="tableColumns" :loading="loading" :rows="monitoringStore.monitoring.length ?? 10"
      :value="monitoringStore.monitoring" :formattingRules="formattingRules" skeletonHeight="23px">
      <template #empty>
        <div class="text-center" style="z-index: 0">{{ $t('emptyMessage') }}</div>
      </template>
    </SrDataTable>
    <Divider class="mb-0 mt-0"></Divider>
    <!-- Форма -->
    <Form class="p-4 grid grid-cols-1 lg:grid-cols-7 gap-2" ref="form" @submit="onFormSubmit" :resolver="resolver">
      <!-- Биржа покупки -->
      <FormField name="exBuy">
        <FloatLabel variant="on">
          <Select :options="AVAILABLE_EXCHANGES" optionLabel="title" optionValue="value" fluid size="small">
          </Select>
          <label>{{ $t("exBuy") }}</label>
        </FloatLabel>
      </FormField>

      <!-- Биржа продажи -->
      <FormField name="exSell">
        <FloatLabel variant="on">
          <Select :options="AVAILABLE_EXCHANGES" optionLabel="title" optionValue="value" fluid size="small">
          </Select>
          <label>{{ $t("exSell") }}</label>
        </FloatLabel>
      </FormField>

      <!-- Торговая пара -->
      <FormField name="asset">
        <FloatLabel variant="on">
          <InputText fluid size="small" />
          <label>{{ $t("symbol") }}</label>
        </FloatLabel>
      </FormField>

      <!-- Тип связки -->
      <FormField name="marketType">
        <FloatLabel variant="on">
          <Select :options="marketTypes" optionLabel="title" optionValue="value" fluid size="small">
          </Select>
          <label>{{ $t("marketType") }}</label>
        </FloatLabel>
      </FormField>

      <!-- Спред -->
      <FormField name="spread">
        <FloatLabel variant="on">
          <SrInputNumber fluid size="small">
          </SrInputNumber>
          <label>{{ $t("spread") }}</label>
        </FloatLabel>
      </FormField>

      <!-- Обратный спред -->
      <FormField name="backspread">
        <FloatLabel variant="on">
          <SrInputNumber fluid size="small">
          </SrInputNumber>
          <label>{{ $t("backspread") }}</label>
        </FloatLabel>
      </FormField>

      <!-- Кнопка "далее" -->
      <Button class="lg:col-span-1" type="submit" size="small" fluid severity="primary" variant="outlined"
        :label="$t('watchBunch')"></Button>
    </Form>
  </AdaptiveDialog>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import AdaptiveDialog from '../AdaptiveDialog.vue';
import SrDataTable from '@/components/tables/SrDataTable.vue';
import Message from 'primevue/message'
import { useMonitoringStore } from '@/stores/monitoringsStore';
import InputText from 'primevue/inputtext'
import FloatLabel from "primevue/floatlabel"
import Button from "primevue/button"
import { getMarketSpreadAlertsTableColumns } from '@/columns/monitoringSpreadAlertsColumns';
import { Form, FormField, type FormInstance, type FormSubmitEvent } from '@primevue/forms';
import { Divider, Select } from 'primevue';
import SrInputNumber from '../SrInputNumber.vue';
import { AVAILABLE_EXCHANGES } from '@/utils/consts';
import { i18n } from '@/i18n';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import z from 'zod';
import { useMyToast } from '@/composable/useMyToast';
import { isNull, isString, isUndefined } from 'lodash';
import { useAutoRefresh } from '@/composable/useAutorefresh';

const { t } = i18n.global
const form = useTemplateRef<FormInstance>("form")
const loading = ref(true)

const refresher = useAutoRefresh(async () => {
  await monitoringStore.fetchMonitorings()
}, 5000)

const monitoringStore = useMonitoringStore()
const visible = ref(false)
const tableColumns = getMarketSpreadAlertsTableColumns(form)

const resolver = zodResolver(
  z.object({
    asset: z.string().min(1, { message: t('assetEmptyError') }),
    exBuy: z.string().min(1, { message: t('cexEmptyError') }),
    exSell: z.string().min(1, { message: t('cexEmptyError') }),
    marketType: z.boolean(),
    spread: z.preprocess(
      (x: string | null) => (isString(x) && x.trim() === '' ? null : Number(x)),
      z.number().nullable(),
    ),
    backspread: z.preprocess(
      (x: string | null) => (isString(x) && x.trim() === '' ? null : Number(x)),
      z.number().nullable(),
    ),
  })
)

const formattingRules = {
  spread: (v: number | null) => {
    return !isNull(v) ? v.toFixed(2) + '%' : '—'
  },
  backspread: (v: number | null) => {
    return !isNull(v) ? v.toFixed(2) + '%' : '—'
  },
  "symbol.name": (v: string) => {
    return v.split('/')[0]
  },
  "is_spot": (v: boolean) => {
    return v ? t("spotSwap") : t("swap")
  }
}

const marketTypes = [
  {
    title: t("spotSwap"),
    value: true
  },
  {
    title: t("swap"),
    value: false
  },
]

const { addToast } = useMyToast()
const onFormSubmit = async (e: FormSubmitEvent) => {
  if (!e.valid) {
    return
  }
  if ((isNull(e.values.spread) || isUndefined(e.values.spread)) && (isNull(e.values.backspread) || isUndefined(e.values.backspread))) {
    addToast({
      message: t('spreadAndBackspreadAreEmpty'),
      severity: "error"
    })
    return
  }
  loading.value = true
  try {
    await monitoringStore.saveMonitoring(
      e.values.exBuy,
      e.values.exSell,
      e.values.asset,
      e.values.spread,
      e.values.backspread,
      e.values.marketType
    )
    await monitoringStore.fetchMonitorings()
  } finally {
    loading.value = false
  }
}

const toggle = () => {
  visible.value = !visible.value
}

const onShow = async () => {
  await monitoringStore.fetchMonitorings()
  loading.value = false
  refresher.startRefresh()
}

const onHide = async () => {
  refresher.stopRefresh()
}

onUnmounted(() => {
  refresher.stopRefresh()
})

onMounted(async () => {
  loading.value = false
})

defineExpose({ toggle })
</script>
